import { CircularProgress, Grid, Icon, Pagination, Paper } from '@mui/material';
import { GridSize } from '@mui/material/Grid';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useJobmarketInvoicesQuery } from 'api/hooks/jobmarket/invoices';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { InvoiceItem } from 'app/components/RichList/items/InvoiceItem';
import { translations } from 'locales/i18n';

import '../styles/InvoiceManagement.scss';

interface Props {
  paid?: boolean;
  company?: string;
}

export function Invoices({ paid, company }: Props) {
  const { t } = useTranslation();
  const [offset, setOffset] = useState(0);
  const companyId = company ? parseInt(company, 10) : undefined;

  const { data: invoicesListData, isLoading } = useJobmarketInvoicesQuery({
    params: {
      paid,
      offset: offset,
      limit: 10,
      companyId
    }
  });

  // const getInvoicesQuery = useQuery(QUERY, {
  //   variables: {
  //     filter: {
  //       paid: paid
  //     },
  //     pagination: {
  //       offset: 0,
  //       limit: 10
  //     },
  //     companyId: companyId
  //   },
  //   fetchPolicy: 'network-only',
  //   context: { clientName: 'federation' }
  // });
  // const { refetch } = getInvoicesQuery;

  const handlePagination = (_: React.ChangeEvent<unknown>, page: number) => {
    const offset = (page - 1) * 10;
    setOffset(offset);
  };

  const buildListHeading = (items: Array<{ weight?: GridSize; title: string; skip?: boolean }>) => {
    return items
      .map(item => {
        if (item.skip) {
          return null;
        }
        return (
          <Grid item md={item.weight} className="invoice-management__list-heading__item">
            {item.title}
          </Grid>
        );
      })
      .filter(i => i !== null);
  };

  const getListHeading = () => {
    if (company) {
      return buildListHeading([
        { weight: 1, title: t(translations.common.id()) },
        { weight: 2, title: t(translations.pages.invoices.ksNumber()) },
        { weight: 2, title: t(translations.pages.invoices.stokNumber()) },
        { weight: 2, title: t(translations.pages.invoices.createdAt()) },
        { weight: 2, title: t(translations.pages.invoices.amount()) },
        // { weight: 2, title: 'PDF' },
        {
          weight: 3,
          title: paid
            ? t(translations.pages.invoices.paymentDate())
            : t(translations.pages.invoices.actions())
        }
      ]);
    }

    return buildListHeading([
      { weight: 1, title: t(translations.common.id()) },
      { weight: 3, title: t(translations.common.company()) },
      { weight: 1, title: t(translations.pages.invoices.offerId()) },
      { weight: 2, title: t(translations.pages.invoices.ksNumber()) },
      { weight: 2, title: t(translations.pages.invoices.stokNumber()) },
      { weight: 1, title: t(translations.pages.invoices.createdAt()) },
      { weight: !paid ? 1 : 2, title: t(translations.pages.invoices.amount()) },
      { weight: 1, title: t(translations.pages.invoices.actions()) }
    ]);
  };

  let invoicesCount;
  switch (true) {
    case paid:
      invoicesCount = invoicesListData?.paidInvoicesCount;
      break;
    default:
      invoicesCount = invoicesListData?.openInvoicesCount;
      break;
  }

  if (isLoading) {
    return <CircularProgress />;
  }

  const invoices = invoicesListData!;

  return (
    <ErrorBoundary boundaryTag="articlesListBoundary">
      <>
        <RichList
          staticHeading={
            <Grid
              container
              className="invoice-management__list-heading"
              component={Paper}
              elevation={3}
              square
            >
              {getListHeading()}
            </Grid>
          }
        >
          {invoices.data.length === 0 ? (
            <NoItems
              icon={<Icon className="fas fa-info-circle" />}
              message={t(translations.pages.invoices.noInvoices())}
            />
          ) : (
            <>
              <Grid
                container
                component={Paper}
                elevation={1}
                square
                className="invoice-management__list"
              >
                {invoices.data.map(i => (
                  <ErrorBoundary key={i.id} boundaryTag="invoiceItemBoundary">
                    <InvoiceItem invoice={i} key={i.id} company={!!company} />
                  </ErrorBoundary>
                ))}
              </Grid>
            </>
          )}
        </RichList>
        {Math.floor(invoicesCount / 10) > 0 && (
          <Grid container className="invoice-management__pagination">
            <Pagination
              count={Math.ceil(invoicesCount / 10)}
              onChange={handlePagination}
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
              size="medium"
              color="primary"
              siblingCount={2}
              boundaryCount={2}
            />
          </Grid>
        )}
      </>
    </ErrorBoundary>
  );
}
